import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  ModuleDocument,
  ModuleMapEntry,
  ModuleMetadata,
  ModuleStandardsResponse,
  SidebarGroup,
} from '@one/module-sdk';
import {
  Badge,
  Button,
  Card,
  Dot,
  EmptyState,
  Field,
  Notice,
  PageHead,
  Progress,
  SearchInput,
  Section,
  Select,
  Skeleton,
  Table,
  Tabs,
} from '@one/ui';
import { apiFetch, hatRecht, type ShellServices } from './context';
import type { ManifestEntry } from './types';

/**
 * Modulinformationen nach ADR-008 (Aufgabenplan, Doku, Daten & Verbindungen,
 * Protokoll, Modul-Landkarte) — einheitlich von der Shell für jedes Modul.
 *
 * Im Designsystem sind das Seiten der Sidebar in einer eigenen Gruppe „Modul",
 * unter den Seiten, die das Modul selbst meldet. Das Modul bleibt dabei gemountet
 * (verborgen statt entfernt): Wer die Hilfe liest, verliert keine Eingaben.
 */
export type InfoSeite = 'plan' | 'doku' | 'daten' | 'protokoll' | 'landkarte';

/** Präfix der Shell-eigenen Sidebar-Pfade; Module verwenden es nie. */
export const INFO_PRAEFIX = '@modul/';

const TITEL: Record<InfoSeite, string> = {
  plan: 'Aufgabenplan',
  doku: 'Doku',
  daten: 'Daten & Verbindungen',
  protokoll: 'Protokoll',
  landkarte: 'Modul-Landkarte',
};
const ICON: Record<InfoSeite, string> = {
  plan: 'list-checks',
  doku: 'book-open',
  daten: 'network',
  protokoll: 'history',
  landkarte: 'map',
};

export function darfLandkarte(entry: ManifestEntry, services: ShellServices): boolean {
  return entry.id === 'verwaltung' && hatRecht(services.bootstrap.permissions, 'core.module.view');
}

/** Die Sidebar-Gruppe „Modul" mit den Shell-eigenen Seiten. */
export function infoGruppe(entry: ManifestEntry, services: ShellServices): SidebarGroup {
  const seiten: InfoSeite[] = ['plan', 'doku', 'daten', 'protokoll'];
  if (darfLandkarte(entry, services)) seiten.push('landkarte');
  return {
    title: 'Modul',
    items: seiten.map((s) => ({ path: `${INFO_PRAEFIX}${s}`, label: TITEL[s], icon: ICON[s] })),
  };
}

export function infoSeiteAus(pfad: string | undefined): InfoSeite | null {
  if (!pfad?.startsWith(INFO_PRAEFIX)) return null;
  const s = pfad.slice(INFO_PRAEFIX.length) as InfoSeite;
  return s in TITEL ? s : null;
}

export interface ModulStandards {
  data: ModuleStandardsResponse | null;
  error: string;
  erneut: () => void;
}

/** Lädt Steckbrief und Laufzeitstatus; ein Ausfall blockiert das Modul nie. */
export function useModulStandards(entry: ManifestEntry | null, mandant: string): ModulStandards {
  const [data, setData] = useState<ModuleStandardsResponse | null>(null);
  const [error, setError] = useState('');
  const [versuch, setVersuch] = useState(0);
  useEffect(() => {
    if (!entry) return;
    const abort = new AbortController();
    setData(null);
    setError('');
    void apiFetch(
      `core/module-standards/${encodeURIComponent(entry.id)}`,
      { signal: abort.signal },
      mandant,
    )
      .then(async (r) => {
        if (!r.ok) throw new Error('Metadaten nicht verfügbar');
        return r.json() as Promise<ModuleStandardsResponse>;
      })
      .then((d) => {
        if (!abort.signal.aborted) setData(d);
      })
      .catch(() => {
        if (!abort.signal.aborted)
          setError(
            'Die Modulinformationen konnten nicht geladen werden. Die Anwendung bleibt verfügbar.',
          );
      });
    return () => abort.abort();
  }, [entry?.id, entry?.url, mandant, versuch]);
  return { data, error, erneut: () => setVersuch((n) => n + 1) };
}

const TASK_TON = {
  erledigt: 'good',
  in_arbeit: 'accent',
  review: 'accent',
  offen: 'neutral',
  verworfen: 'neutral',
} as const;

function DocumentView({ documents, initial }: { documents: ModuleDocument[]; initial?: string }) {
  const [selected, setSelected] = useState(initial ?? documents[0]?.id ?? '');
  const document = documents.find((d) => d.id === selected) ?? documents[0];
  if (!document)
    return (
      <EmptyState icon="book-open" title="Noch keine Dokumentation">
        Für diesen Bereich ist noch nichts hinterlegt.
      </EmptyState>
    );
  return (
    <div className="one-stack">
      <div style={{ maxWidth: 360 }}>
        <Field label="Kapitel">
          {(id) => (
            <Select id={id} value={document.id} onChange={(e) => setSelected(e.target.value)}>
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <Card>
        <article className="module-markdown shell-markdown">
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml
            components={{
              img: ({ alt }) => <span>{alt ? `[Abbildung: ${alt}]` : '[Abbildung]'}</span>,
              a: ({ href, children }) => {
                if (!href) return <span>{children}</span>;
                if (/^https?:\/\//i.test(href)) {
                  try {
                    const url = new URL(href);
                    return !url.username && !url.password ? (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ) : (
                      <span>{children}</span>
                    );
                  } catch {
                    return <span>{children}</span>;
                  }
                }
                let path: string;
                try {
                  path = new URL(href, `https://module.invalid/${document.id}`).pathname.slice(1);
                } catch {
                  return <span>{children}</span>;
                }
                return documents.some((d) => d.id === path) ? (
                  <button
                    type="button"
                    className="shell-doc-link"
                    onClick={() => setSelected(path)}
                  >
                    {children}
                  </button>
                ) : (
                  <span title="Verweis im Quellcode">{children}</span>
                );
              },
            }}
          >
            {document.markdown}
          </Markdown>
        </article>
      </Card>
    </div>
  );
}

function Plan({ metadata: m }: { metadata: ModuleMetadata }) {
  return (
    <>
      <Card>
        <div className="one-kpi-label">
          <span>
            {m.progress.done} von {m.progress.total} Aufgaben erledigt · {m.progress.discarded}{' '}
            verworfen
          </span>
          <span className="mono">
            T{m.progress.done}/{m.progress.total}
          </span>
        </div>
        <Progress
          value={m.progress.total ? m.progress.done / m.progress.total : 0}
          label="Fortschritt des Aufgabenplans"
        />
      </Card>
      {m.plan.meilensteine.map((ms) => (
        <Section key={ms.id} title={`${ms.id} · ${ms.titel}`}>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Aufgabe</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {m.plan.tasks
                .filter((t) => t.meilenstein === ms.id)
                .map((t) => (
                  <tr key={t.id}>
                    <td className="mono">{t.id}</td>
                    <td className="one-wrap">
                      {t.titel}
                      {t.begruendung && <div className="muted">{t.begruendung}</div>}
                    </td>
                    <td>
                      <Badge
                        case="normal"
                        tone={TASK_TON[t.status as keyof typeof TASK_TON] ?? 'neutral'}
                      >
                        {t.status.replaceAll('_', ' ')}
                      </Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Section>
      ))}
    </>
  );
}

function Verbindungsstatus({
  data,
  error,
}: {
  data: ModuleStandardsResponse | null;
  error: string;
}) {
  const zustand = error ? 'unavailable' : (data?.runtime.state ?? 'loading');
  const text =
    zustand === 'unavailable'
      ? 'Verbindungen · Status unbekannt'
      : zustand === 'unused'
        ? 'Verbindungen · geplant / ungenutzt'
        : zustand === 'loading'
          ? 'Verbindungen · wird geladen'
          : `Verbindungen · ${zustand}`;
  const ton = zustand === 'unavailable' ? 'warn' : zustand === 'unused' ? 'muted' : 'info';
  return (
    <span className="one-row">
      <Dot tone={ton} aria-hidden />
      {text}
    </span>
  );
}

function DataPanel({ data }: { data: ModuleStandardsResponse }) {
  const [filter, setFilter] = useState('');
  const [source, setSource] = useState('alle');
  const [access, setAccess] = useState('alle');
  const [personal, setPersonal] = useState('alle');
  const m = data.metadata;
  const fields = [
    ...(m?.daten.one ?? []).map((f) => ({ ...f, source: f.weg ?? 'db' })),
    ...(m?.daten.bridge ?? []).flatMap((c) =>
      (c.felder ?? []).map((f) => ({
        feld: `${c.sicht ?? c.verbindung}.${f}`,
        zugriff: c.zugriff ?? 'lesen',
        personenbezogen: null,
        weg: 'bridge',
        stand: c.stand,
        hinweis: c.quelle ?? c.verbindung,
        source: 'bridge',
      })),
    ),
  ];
  const visible = fields.filter(
    (f) =>
      (source === 'alle' || f.source === source) &&
      (access === 'alle' || f.zugriff === access) &&
      (personal === 'alle' || String(f.personenbezogen) === personal) &&
      `${f.feld} ${f.hinweis ?? ''}`.toLocaleLowerCase().includes(filter.toLocaleLowerCase()),
  );
  return (
    <>
      <Notice tone="info">{data.runtime.message}</Notice>
      <Section title="Verbindungen laut Datenvertrag">
        <Table>
          <thead>
            <tr>
              <th>Quelle</th>
              <th>Weg / Objekt</th>
              <th>Richtung</th>
              <th>Stand</th>
              <th>Frischevorgabe</th>
              <th>Läufe / Fehler</th>
            </tr>
          </thead>
          <tbody>
            {m?.daten.bridge.map((c, i) => (
              <tr key={`${c.verbindung}-${i}`}>
                <td>
                  {c.quelle ?? c.verbindung}
                  <div className="muted">{c.pflicht ? 'Pflichtverbindung' : 'optional'}</div>
                </td>
                <td>
                  {c.art ?? 'Bridge'}
                  {c.sicht && <div className="muted mono">{c.sicht}</div>}
                </td>
                <td>{c.zugriff ?? 'noch offen'}</td>
                <td>
                  <Badge case="normal" tone={c.stand === 'aktiv' ? 'good' : 'neutral'}>
                    {c.stand}
                  </Badge>
                </td>
                <td>{c.frische ?? 'noch offen'}</td>
                <td>
                  {c.stand === 'geplant' ? 'noch keine Läufe' : 'Laufzeitstatus nicht verfügbar'}
                </td>
              </tr>
            ))}
            {!m?.daten.bridge.length && (
              <tr>
                <td colSpan={6} className="muted">
                  Keine Bridge-Verbindung deklariert.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {m?.daten.extern?.map((c) => (
          <Notice key={c.system} tone="warn" title={`${c.system} · extern · Übergang`}>
            {c.zweck}
          </Notice>
        ))}
        {m?.daten.ki?.map((c) => (
          <Notice key={c.zweck} tone="accent" icon="sparkles" title={`KI · ${c.stand}`}>
            {c.zweck} · {c.weg}
          </Notice>
        ))}
      </Section>
      <Section title="Feld-Inventar">
        <div className="one-toolbar">
          <div style={{ minWidth: 240, flex: '1 1 240px' }}>
            <SearchInput
              label="Feld oder Herkunft suchen"
              placeholder="Feld oder Herkunft suchen"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <Select
            aria-label="Datenweg"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="alle">Alle Datenwege</option>
            {[...new Set(fields.map((f) => f.source))].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Zugriff"
            value={access}
            onChange={(e) => setAccess(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="alle">Jeder Zugriff</option>
            <option value="lesen">Lesen</option>
            <option value="schreiben">Schreiben</option>
            <option value="lesen_schreiben">Lesen und schreiben</option>
          </Select>
          <Select
            aria-label="Personenbezug"
            value={personal}
            onChange={(e) => setPersonal(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="alle">Jeder Personenbezug</option>
            <option value="true">Personenbezogen</option>
            <option value="false">Nicht personenbezogen</option>
            <option value="null">Nicht deklariert</option>
          </Select>
        </div>
        <Table
          foot={
            <span>
              {visible.length} von {fields.length} Feldern
            </span>
          }
        >
          <thead>
            <tr>
              <th>Feld / Objekt</th>
              <th>Weg</th>
              <th>Zugriff</th>
              <th>Personenbezug</th>
              <th>Stand</th>
              <th>Typ / Herkunft / Hinweis</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((f, i) => (
              <tr key={`${f.feld}-${i}`}>
                <td className="mono">{f.feld}</td>
                <td>{f.source}</td>
                <td>{f.zugriff.replaceAll('_', ' ')}</td>
                <td>
                  {f.personenbezogen === null ? (
                    <Badge case="normal">offen</Badge>
                  ) : f.personenbezogen ? (
                    <Badge case="normal" tone="warn">
                      ja
                    </Badge>
                  ) : (
                    'nein'
                  )}
                </td>
                <td>{f.stand}</td>
                <td className="one-wrap">{f.hinweis ?? 'nicht hinterlegt'}</td>
              </tr>
            ))}
            {!visible.length && (
              <tr>
                <td colSpan={6} className="muted">
                  Keine passenden Felder.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Section>
    </>
  );
}

type Quelle = 'versionen' | 'entscheidung' | 'betrieb' | 'audit';

function Protocol({ metadata, services }: { metadata: ModuleMetadata; services: ShellServices }) {
  const [kind, setKind] = useState<Quelle>('versionen');
  return (
    <>
      <Tabs<Quelle>
        label="Protokollquellen"
        value={kind}
        onChange={setKind}
        items={[
          { id: 'versionen', label: 'Versionen' },
          { id: 'entscheidung', label: 'Entscheidungen' },
          { id: 'betrieb', label: 'Betrieb' },
          { id: 'audit', label: 'Fachliches Audit' },
        ]}
      />
      {(kind === 'versionen' || kind === 'entscheidung') && (
        <DocumentView key={kind} documents={metadata.documents.filter((d) => d.kind === kind)} />
      )}
      {kind === 'betrieb' && (
        <Notice tone="info">
          Eine gemeinsame Quelle für Deployment- und Bridge-Laufereignisse ist noch nicht
          angebunden. Es werden keine Ereignisse aus dem personenbezogenen Audit übernommen.
        </Notice>
      )}
      {kind === 'audit' && (
        <Notice
          tone="info"
          icon="shield"
          action={
            hatRecht(services.bootstrap.permissions, 'core.audit.read') &&
            services.bootstrap.modules.some((m) => m.id === 'verwaltung') ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => services.navigate('/verwaltung/audit')}
              >
                Audit in der Verwaltung öffnen
              </Button>
            ) : undefined
          }
        >
          Das fachliche Audit ist gesondert geschützt. Modulzugriff erteilt kein Audit-Leserecht.
          Die bestehenden Freigaben und Vier-Augen-Regeln gelten weiterhin.
        </Notice>
      )}
    </>
  );
}

function ModuleMap({ tenant }: { tenant: string }) {
  const [entries, setEntries] = useState<ModuleMapEntry[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    void apiFetch('core/module-standards/map', { signal: abort.signal }, tenant)
      .then(async (r) => {
        if (!r.ok) throw new Error('Modul-Landkarte ist nicht verfügbar.');
        return r.json() as Promise<ModuleMapEntry[]>;
      })
      .then((d) => {
        if (!abort.signal.aborted) setEntries(d);
      })
      .catch(() => {
        if (!abort.signal.aborted) setError('Modul-Landkarte konnte nicht geladen werden.');
      });
    return () => abort.abort();
  }, [tenant]);
  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!entries) return <Skeleton height={160} />;
  return (
    <Table>
      <thead>
        <tr>
          <th>Modul</th>
          <th>Phase</th>
          <th className="num">Aufgaben</th>
          <th>Dokustand</th>
          <th>Ausgerollt</th>
          <th>Owner</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((m) => (
          <tr key={m.id}>
            <td>
              {m.title}
              <div className="muted">
                {m.documented ? 'ADR-008-Metadaten vorhanden' : 'Steckbrief fehlt noch'}
              </div>
            </td>
            <td>{m.phase ? <Badge>{m.phase}</Badge> : <span className="muted">offen</span>}</td>
            <td className="num">{m.progress ? `T${m.progress.done}/${m.progress.total}` : '—'}</td>
            <td className="mono">{m.version ?? '—'}</td>
            <td className="mono">{m.deployedVersion ?? 'nicht angegeben'}</td>
            <td>{m.owner ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Eine Shell-eigene Modulseite im Inhaltsbereich. */
export function ModulInfo({
  seite,
  entry,
  services,
  standards,
}: {
  seite: InfoSeite;
  entry: ManifestEntry;
  services: ShellServices;
  standards: ModulStandards;
}) {
  const { data, error, erneut } = standards;
  const m = data?.metadata;
  const sub = (
    <span className="one-row" data-gap="4">
      <span>Ausgerollt: {data?.deployedVersion ?? 'nicht angegeben'}</span>
      {m && <span>Dokustand: {m.version}</span>}
      <Verbindungsstatus data={data} error={error} />
    </span>
  );

  let inhalt;
  if (seite === 'landkarte' && darfLandkarte(entry, services)) {
    inhalt = (
      <>
        <p className="one-prose">
          Modulkatalog der Umgebung. Diese Übersicht ändert keine Freischaltungen.
        </p>
        <ModuleMap tenant={services.activeTenant.slug} />
      </>
    );
  } else if (error) {
    inhalt = (
      <Notice
        tone="danger"
        action={
          <Button size="sm" variant="secondary" onClick={erneut}>
            Erneut laden
          </Button>
        }
      >
        {error}
      </Notice>
    );
  } else if (!data) {
    inhalt = <Skeleton height={160} />;
  } else if (!m) {
    inhalt = (
      <EmptyState icon="file-search" title="Noch kein Steckbrief">
        Für dieses Modul ist noch kein ADR-008-Steckbrief hinterlegt. Die Anwendung kann unverändert
        genutzt werden.
      </EmptyState>
    );
  } else {
    inhalt = (
      <>
        {data.deployedVersion && data.deployedVersion !== m.version && (
          <Notice tone="warn">
            Dokumentation und ausgerolltes Modul haben unterschiedliche Versionen. Die Angaben
            beziehen sich auf Dokustand {m.version}.
          </Notice>
        )}
        {seite === 'plan' && <Plan metadata={m} />}
        {seite === 'doku' && (
          <DocumentView
            documents={[...m.documents].sort(
              (a, b) => Number(b.kind === 'hilfe') - Number(a.kind === 'hilfe'),
            )}
          />
        )}
        {seite === 'daten' && <DataPanel data={data} />}
        {seite === 'protokoll' && <Protocol metadata={m} services={services} />}
      </>
    );
  }

  return (
    <>
      <PageHead title={TITEL[seite]} sub={seite === 'landkarte' ? undefined : sub} />
      {inhalt}
    </>
  );
}

export const infoTitel = (s: InfoSeite) => TITEL[s];
